import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Modal } from '@mui/material';
import { RxReload } from 'react-icons/rx';
import { FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { formatDate, getUserInfoDetails, mapToOption, formatDateCST, formatTimeCST } from '../../../utlis';
import { createProject, updateProject } from '../../../api/candidiate';
import { getCandidateById } from '../../../api/candidiate';
import useFetch from '../../../hooks/useFetch';
import MyTable from '../../../components/MyTable';
import axiosInstance from '../../../api/axiosInstance';
import { setBasicInfo, setCandidateId, setProfessionalInfo, setProfileInfo } from '../../../features/candidate/personalSlice';

// Helper to convert API date (MM/DD/YYYY or ISO) to input format (YYYY-MM-DD)
const formatDateForForm = (dateString) => {
  if (!dateString) return '';
  if (dateString.includes('/')) {
    const [month, day, year] = dateString.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

// Helper to convert date format for navigation
function convertDateFormat(inputDate) {
  if (!inputDate || typeof inputDate !== 'string') return inputDate;
  const parts = inputDate.split('/');
  if (parts.length === 3) {
    const [month, day, year] = parts;
    return `${year}-${month}-${day}`;
  }
  return inputDate;
}

const initialProject = {
  projectId: '',
  candidateId: '',
  jobTitle: '',
  jobId: '',
  endClientName: '',
  division: '',
  remoteWork: 'Y',
  vendorId: '',
  vendorContactId: '',
  salesMgrId: '',
  hrContactId: '',
  accountingContactId: '',
  client: '',
  city: '',
  state: '',
  startDate: '',
  estimatedEndDate: '',
  billingRate: '',
  payType: '',
  paymentTerms: '',
  firstInvoiceDate: '',
  lastInvoiceDate: '',
  nextInvoiceDate: '',
  invoiceContactEmail: '',
  backgroundProcessType: '',
  backgroundProcessDesc: '',
  backgroundStartOn: '',
  backgroundEndOn: '',
  backgroundStatus: '',
  projectStatus: 'Active',
  statusReason: '',
  created: '',
  created_by: '',
  updated: '',
  updated_by: '',
};

const SalesProjects = () => {
  const userInfo = getUserInfoDetails() || {};
  const { companyId, clientId } = userInfo;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const referenceData = useSelector((state) => state.reference);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({ mode: 'onChange', shouldUnregister: true });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(initialProject);
  const [isLoading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [data, setData] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');

  // Reference data
  const { data: refCandidate, loading: refCandidateLoading } = useFetch('/candidates');
  const { data: refVendor, loading: refVendorLoading } = useFetch('/vendors', {
    company: companyId, client: clientId,
  });
  const { data: refSalesPerson, loading: refSalesLoading } = useFetch('/salesteam', {
    company: companyId, client: clientId,
  });
  const { data: refHRContacts, loading: refHRContactsLoading } = useFetch('/contacts', {
    company: companyId, client: clientId, contactType: 'HR',
  });
  const { data: refAcctContacts, loading: refAcctContactsLoading } = useFetch('/contacts', {
    company: companyId, client: clientId, contactType: 'Accounting',
  });
  const { data: refVendorContacts, loading: refVendorContactsLoading } = useFetch('/contacts', {
    company: companyId, client: clientId, contactType: 'Vendor',
  });

  const hrContactRecords = refHRContacts?.data?.records || refHRContacts?.response?.data || [];
  const acctContactRecords = refAcctContacts?.data?.records || refAcctContacts?.response?.data || [];
  const vendorContactRecords = refVendorContacts?.data?.records || refVendorContacts?.response?.data || [];
  const candidateRecords = refCandidate?.data?.records || refCandidate?.response?.data || [];

  // Handle candidate click - navigate to candidate page
  const handleCandidateClick = async (candidateId) => {
    if (!candidateId) {
      toast.error('Candidate ID not found');
      return;
    }

    let r = candidateRecords.find(c => c.candidate_id === candidateId || c.candidate_id === Number(candidateId));

    if (!r) {
      setLoading(true);
      try {
        const res = await getCandidateById(candidateId);
        r = res.data?.data || res.data?.response?.data || res.data || null;
      } catch (err) {
        toast.error('Failed to load candidate details');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    if (!r) {
      toast.error('Candidate not found');
      return;
    }

    dispatch(setBasicInfo({
      firstName: r.first_name || '',
      lastName: r.last_name || '',
      middleName: r.middle_name || '',
      gender: r.gender ? r.gender.toLowerCase() : '',
      maritalStatus: r.marital_status ? r.marital_status.toLowerCase() : '',
      dob: convertDateFormat(r.dob) || '',
      email: r.email_id || '',
      address1: r.address1 || '',
      address2: r.address2 || '',
      phone: r.phone || '',
      alternatePhoneNum: r.mobile || ''
    }));
    dispatch(setProfessionalInfo({
      jobTitle: r.job_title || '',
      salesPerson: r.sales_person_id || '',
      technology: r.division || '',
      workPermitStatus: r.status || '',
      workStatusIssueDate: convertDateFormat(r.work_status_issue_date) || '',
      workStatusEndDate: convertDateFormat(r.work_status_enddate) || '',
      SSN: r.ssn || '',
      employmentStatus: r.status || '',
      referredBy: r.referred_by || ''
    }));
    dispatch(setProfileInfo({
      totalInterviews: r.total_interviews || 0,
      activeInterviews: r.total_active_interviews || 0,
      passedInterviews: r.total_passed_interviews || 0,
      failedInterviews: r.total_failed_interviews || 0,
      holdInterviews: r.total_onhold_interviews || 0,
      lastInterviewOn: convertDateFormat(r.last_interview_on) || '',
      rating: r.rating || 0
    }));
    dispatch(setCandidateId(candidateId));
    navigate('/candidate/add-candidate/personal/basic');
  };

  // Data fetching - all projects, filtered by status
  const getData = async (showToast = false, status = statusFilter) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/projects', {
        params: { company: companyId, client: clientId }
      });
      const projectData = res.data?.response?.data || res.data?.data?.records || res.data?.records || res.data || [];
      const allProjects = Array.isArray(projectData) ? projectData : [];
      // Filter by selected status
      const filteredProjects = status
        ? allProjects.filter(p => (p.status || '').toLowerCase() === status.toLowerCase())
        : allProjects;
      setData(filteredProjects);
      if (showToast) {
        toast.success('Data refreshed successfully', { toastId: 'refresh-success' });
      }
    } catch (err) {
      const statusCode = err.response?.status;
      if (err.message === 'Network Error' || !err.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 403) {
        toast.error('Access denied: You do not have permission to view projects');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else {
        toast.error('Failed to load projects');
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // Re-fetch when status filter changes
  useEffect(() => {
    getData(false, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Search filter
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const searchLower = searchTerm.toLowerCase();
    return data.filter((item) =>
      (item.candidate_name || '').toLowerCase().includes(searchLower) ||
      (item.job_title || '').toLowerCase().includes(searchLower) ||
      (item.vendor_name || '').toLowerCase().includes(searchLower) ||
      (item.end_client_name || '').toLowerCase().includes(searchLower) ||
      (item.city || '').toLowerCase().includes(searchLower) ||
      (item.state || '').toLowerCase().includes(searchLower) ||
      (item.status || '').toLowerCase().includes(searchLower) ||
      (item.sales_mgr_name || '').toLowerCase().includes(searchLower)
    );
  }, [data, searchTerm]);

  // Modal handlers
  const openModalForNew = () => {
    setCurrentProject(initialProject);
    reset(initialProject);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentProject(initialProject);
    reset(initialProject);
  };

  const prepareUpdate = (projectId) => {
    const p = data.find((d) => d.project_id === projectId || d.record_id === projectId);
    if (!p) {
      toast.error('Project not found');
      return;
    }

    const editData = {
      projectId: p.project_id || p.record_id,
      candidateId: p.candidate_id || '',
      jobTitle: p.job_title || '',
      jobId: p.job_id || '',
      endClientName: p.end_client_name || '',
      division: p.division || '',
      remoteWork: p.remote_work || 'Y',
      vendorId: p.vendor_id || '',
      vendorContactId: p.vendor_contact_id || '',
      salesMgrId: p.sales_mgr_id || '',
      hrContactId: p.hr_contact_id || '',
      accountingContactId: p.accounting_contact_id || '',
      client: p.client || '',
      city: p.city || '',
      state: p.state || '',
      startDate: formatDateForForm(p.start_date),
      estimatedEndDate: formatDateForForm(p.estimated_end_date),
      billingRate: p.billing_rate || '',
      payType: p.pay_type || '',
      paymentTerms: p.payment_terms || '',
      firstInvoiceDate: formatDateForForm(p.first_invoice_date),
      lastInvoiceDate: formatDateForForm(p.last_invoice_date),
      nextInvoiceDate: formatDateForForm(p.next_invoice_date),
      invoiceContactEmail: p.invoice_contact_email || '',
      backgroundProcessType: p.background_process_type || '',
      backgroundProcessDesc: p.background_process_desc || '',
      backgroundStartOn: formatDateForForm(p.background_start_on),
      backgroundEndOn: formatDateForForm(p.background_end_on),
      backgroundStatus: p.background_status || '',
      projectStatus: p.status || '',
      statusReason: p.status_reason || '',
      created: p.created || '',
      created_by: p.created_by || '',
      updated: p.updated || '',
      updated_by: p.updated_by || '',
    };

    setCurrentProject(editData);
    reset(editData, { keepDefaultValues: false });
    setIsModalOpen(true);
  };

  // Form submit
  const handleFormSubmit = async (formData) => {
    setIsSaving(true);
    try {
      if (currentProject.projectId) {
        await updateProject({ ...formData, candidateId: currentProject.candidateId }, currentProject.projectId);
        toast.success('Project Updated Successfully');
      } else {
        if (!formData.candidateId) {
          toast.error('Please select a candidate');
          setIsSaving(false);
          return;
        }
        await createProject(formData, formData.candidateId);
        toast.success('Project Created Successfully');
      }
      closeModal();
      getData();
    } catch (error) {
      const errMsg = error?.response?.data?.details || error?.response?.data?.message || error.message;
      toast.error(errMsg || 'Failed to save project');
    } finally {
      setIsSaving(false);
    }
  };

  const projectStatus = watch('projectStatus');

  // Column definitions
  const columns = [
    {
      name: 'project_id',
      label: 'ID',
      options: { display: false },
    },
    {
      name: 'record_id',
      label: 'Record ID',
      options: { display: false },
    },
    {
      name: 'candidate_id',
      label: 'Candidate ID',
      options: { display: false },
    },
    {
      name: 'candidate_name',
      label: 'Candidate Name',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const candidateId = tableMeta.rowData[2];
          return (
            <span
              onClick={() => handleCandidateClick(candidateId)}
              className="text-auxiliary-800 font-semibold cursor-pointer hover:underline"
            >
              {value || 'N/A'}
            </span>
          );
        },
      },
    },
    {
      name: 'job_title',
      label: 'Job Title',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const projectId = tableMeta.rowData[0] || tableMeta.rowData[1];
          return (
            <h3
              onClick={() => prepareUpdate(projectId)}
              className="text-base font-bold text-auxiliary-800 cursor-pointer"
            >
              {value || 'N/A'}
            </h3>
          );
        },
      },
    },
    {
      name: 'vendor_name',
      label: 'Vendor',
      options: { filter: true, sort: true },
    },
    {
      name: 'end_client_name',
      label: 'End Client',
      options: { filter: true, sort: true },
    },
    {
      name: 'sales_mgr_name',
      label: 'Sales Manager',
      options: { filter: true, sort: true },
    },
    {
      name: 'city',
      label: 'City',
      options: { filter: true, sort: true },
    },
    {
      name: 'state',
      label: 'State',
      options: { filter: true, sort: true },
    },
    {
      name: 'start_date',
      label: 'Start Date',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (value ? formatDate(value) : ''),
      },
    },
    {
      name: 'estimated_end_date',
      label: 'End Date',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (value ? formatDate(value) : ''),
      },
    },
    {
      name: 'status',
      label: 'Project Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <p className="py-2 rounded-full w-32 text-center text-secondary-500 activecolor">
            {value || 'N/A'}
          </p>
        ),
      },
    },
  ];

  const tableOptions = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: pageSize,
    rowsPerPageOptions: [5, 10, 15, 20, 50, 100, 200],
    onChangeRowsPerPage: (numberOfRows) => setPageSize(numberOfRows),
    selectableRows: 'none',
    elevation: 0,
    filter: true,
    search: false,
    download: true,
    print: true,
    viewColumns: true,
    textLabels: {
      body: {
        noMatch:
          !isLoading && (!filteredData || filteredData.length === 0)
            ? 'Sorry, no matching records found'
            : '',
      },
    },
  };

  // Section header helper
  const SectionHeader = ({ title }) => (
    <div className="border-b border-gray-200 pb-1 mt-2">
      <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">{title}</h3>
    </div>
  );

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <div className="flex justify-between items-end gap-2 p-3">
          <div className="flex items-center gap-3 self-center">
            <button onClick={() => getData(true)} className="cursor-pointer" title="Reload">
              <RxReload size={20} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="flex items-center gap-3 flex-grow p-3 border-2 border-gray-200 bg-gray-100 rounded-lg">
            <FiSearch color="gray" size={20} />
            <input
              className="outline-none w-full h-full bg-gray-100"
              placeholder="Search by candidate, vendor, city, status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-bold text-gray-700 mb-1">Project Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg py-2 px-5 text-sm outline-none focus:border-secondary-500 bg-white min-w-[140px] text-center"
            >
            {(() => {
              const psConfig = referenceData?.ProjectStatuses || {};
              const psArray = Object.values(psConfig).find(v => Array.isArray(v)) || [];
              const fieldKey = psArray.length > 0 && typeof psArray[0] === 'object' ? Object.keys(psArray[0])[0] : null;
              const options = fieldKey
                ? psArray.map(item => item[fieldKey]).filter(Boolean)
                : psArray.map(item => String(item)).filter(Boolean);
              return options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ));
            })()}
            </select>
          </div>
          <div className="flex items-center">
            <button
              onClick={openModalForNew}
              className="bg-secondary-700 text-white py-2 px-5 rounded-full font-normal whitespace-nowrap"
            >
              <span className="text-lg mr-3">+</span>
              New Project
            </button>
          </div>
        </div>

        {/* Table with loading overlay */}
        <div className="relative">
          <MyTable title="" data={isLoading ? [] : filteredData} columns={columns} options={tableOptions} />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Project Modal */}
      <Modal open={isModalOpen} onClose={closeModal}>
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 w-full md:w-[1100px] max-w-5xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
            {/* Saving overlay */}
            {isSaving && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-2"></div>
                  <span className="text-secondary-700 font-medium">Saving...</span>
                </div>
              </div>
            )}

            {/* Title and Close */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-secondary-800">
                {currentProject.projectId ? 'Edit Project' : 'Add Project'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form className="flex flex-col gap-4 md:gap-5" onSubmit={handleSubmit(handleFormSubmit)}>

              {/* === PROJECT INFO === */}
              <SectionHeader title="Project Info" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                {/* Candidate dropdown - only for new project */}
                {!currentProject.projectId && (
                  <div className="flex flex-col w-full space-y-1">
                    <label className="text-text-hint mb-1">
                      Candidate <span className="text-danger-500">*</span>
                    </label>
                    <select
                      {...register('candidateId', { required: 'Candidate is required' })}
                      className="outline-none border-2 border-secondary-500 rounded-lg"
                    >
                      <option value="">Select Candidate</option>
                      {!refCandidateLoading &&
                        candidateRecords.map((c) => (
                          <option key={c.candidate_id} value={c.candidate_id}>
                            {c.first_name} {c.last_name}
                          </option>
                        ))}
                    </select>
                    {errors.candidateId && <span className="text-red-500">{errors.candidateId.message}</span>}
                  </div>
                )}
                {currentProject.projectId && (
                  <div className="flex flex-col w-full space-y-1">
                    <label className="text-text-hint mb-1">Candidate</label>
                    <input
                      type="text"
                      value={data.find(d => String(d.candidate_id) === String(currentProject.candidateId))?.candidate_name || `ID: ${currentProject.candidateId}`}
                      readOnly
                      className="outline-none border-2 border-gray-300 rounded-lg bg-gray-100"
                    />
                  </div>
                )}
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">
                    Job Title <span className="text-danger-500">*</span>
                  </label>
                  <input
                    {...register('jobTitle', { required: 'Job Title is required' })}
                    type="text"
                    placeholder="Job Title"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                  {errors.jobTitle && <span className="text-red-500">{errors.jobTitle.message}</span>}
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">End Client Name</label>
                  <input
                    {...register('endClientName')}
                    type="text"
                    placeholder="End Client Name"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Division</label>
                  <input
                    {...register('division')}
                    type="text"
                    placeholder="Division"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">
                    Vendor <span className="text-danger-500">*</span>
                  </label>
                  <select
                    {...register('vendorId', { required: 'Vendor is required' })}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select Vendor</option>
                    {!refVendorLoading &&
                      mapToOption(refVendor?.response?.data || refVendor?.data?.records || [], 'name', 'vendor_id').map(
                        (option) => (
                          <option key={option.value} value={option.value}>
                            {option.text}
                          </option>
                        )
                      )}
                  </select>
                  {errors.vendorId && <span className="text-red-500">{errors.vendorId.message}</span>}
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">
                    Sales Manager <span className="text-danger-500">*</span>
                  </label>
                  <select
                    {...register('salesMgrId', { required: 'Sales Manager is required' })}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select Sales Manager</option>
                    {!refSalesLoading &&
                      mapToOption(refSalesPerson?.data?.records, 'first_name', 'manager_id').map(
                        (option) => (
                          <option key={option.value} value={option.value}>
                            {option.text}
                          </option>
                        )
                      )}
                  </select>
                  {errors.salesMgrId && <span className="text-red-500">{errors.salesMgrId.message}</span>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Remote Work</label>
                  <div className="flex gap-4 items-center h-10">
                    <label className="flex items-center gap-1">
                      <input {...register('remoteWork')} type="radio" value="Y" /> Yes
                    </label>
                    <label className="flex items-center gap-1">
                      <input {...register('remoteWork')} type="radio" value="N" /> No
                    </label>
                  </div>
                </div>
              </div>

              {/* === LOCATION & CLIENT === */}
              <SectionHeader title="Location & Client" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Client</label>
                  <input
                    {...register('client')}
                    type="text"
                    placeholder="Client"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">City</label>
                  <select
                    {...register('city')}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select City</option>
                    {referenceData?.cities?.city &&
                      mapToOption(referenceData.cities.city, 'name').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">State</label>
                  <select
                    {...register('state')}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select State</option>
                    {referenceData?.states?.state &&
                      mapToOption(referenceData.states.state, 'name', 'code').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* === DATES === */}
              <SectionHeader title="Dates" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">
                    Start Date <span className="text-danger-500">*</span>
                  </label>
                  <input
                    {...register('startDate', { required: 'Start Date is required' })}
                    type="date"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                  {errors.startDate && <span className="text-red-500">{errors.startDate.message}</span>}
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Estimated End Date</label>
                  <input
                    {...register('estimatedEndDate')}
                    type="date"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
              </div>

              {/* === CONTACTS === */}
              <SectionHeader title="Contacts" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Vendor Contact</label>
                  <select
                    {...register('vendorContactId')}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select Vendor Contact</option>
                    {!refVendorContactsLoading &&
                      vendorContactRecords.map((c) => (
                        <option key={c.contact_id} value={c.contact_id}>
                          {c.first_name} {c.last_name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">HR Contact</label>
                  <select
                    {...register('hrContactId')}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select HR Contact</option>
                    {!refHRContactsLoading &&
                      hrContactRecords.map((c) => (
                        <option key={c.contact_id} value={c.contact_id}>
                          {c.first_name} {c.last_name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Accounting Contact</label>
                  <select
                    {...register('accountingContactId')}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select Accounting Contact</option>
                    {!refAcctContactsLoading &&
                      acctContactRecords.map((c) => (
                        <option key={c.contact_id} value={c.contact_id}>
                          {c.first_name} {c.last_name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* === BILLING & INVOICING === */}
              <SectionHeader title="Billing & Invoicing" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Billing Rate</label>
                  <input
                    {...register('billingRate', {
                      pattern: { value: /^\d+(\.\d{1,2})?$/, message: 'Must be a valid rate' },
                    })}
                    type="text"
                    placeholder="0.00"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                  {errors.billingRate && <span className="text-red-500">{errors.billingRate.message}</span>}
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Pay Type</label>
                  <select
                    {...register('payType')}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select Pay Type</option>
                    {referenceData?.paytypes?.Pay &&
                      mapToOption(referenceData.paytypes.Pay, 'type').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Payment Terms</label>
                  <select
                    {...register('paymentTerms')}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select Payment Terms</option>
                    {referenceData?.paymentterms?.payment &&
                      mapToOption(referenceData.paymentterms.payment, 'term').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">First Invoice Date</label>
                  <input
                    {...register('firstInvoiceDate')}
                    type="date"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Next Invoice Date</label>
                  <input
                    {...register('nextInvoiceDate')}
                    type="date"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Last Invoice Date</label>
                  <input
                    {...register('lastInvoiceDate')}
                    type="date"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Invoice Contact Email</label>
                  <input
                    {...register('invoiceContactEmail', {
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: 'Must be a valid email',
                      },
                    })}
                    type="email"
                    placeholder="email@example.com"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                  {errors.invoiceContactEmail && (
                    <span className="text-red-500">{errors.invoiceContactEmail.message}</span>
                  )}
                </div>
              </div>

              {/* === BACKGROUND PROCESS === */}
              <SectionHeader title="Background Process" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Process Type</label>
                  <input
                    {...register('backgroundProcessType')}
                    type="text"
                    placeholder="Process Type"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Background Status</label>
                  <select
                    {...register('backgroundStatus')}
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  >
                    <option value="">Select Status</option>
                    {(() => {
                      const bgConfig = referenceData?.BackgroundCheckStatuses || {};
                      const bgArray = Object.values(bgConfig).find(v => Array.isArray(v)) || [];
                      const fieldKey = bgArray.length > 0 && typeof bgArray[0] === 'object' ? Object.keys(bgArray[0])[0] : null;
                      return fieldKey
                        ? mapToOption(bgArray, fieldKey).map((option) => (
                            <option key={option.value} value={option.value}>{option.text}</option>
                          ))
                        : bgArray.map((item, i) => (
                            <option key={i} value={String(item)}>{String(item)}</option>
                          ));
                    })()}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Background Start Date</label>
                  <input
                    {...register('backgroundStartOn')}
                    type="date"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Background End Date</label>
                  <input
                    {...register('backgroundEndOn')}
                    type="date"
                    className="outline-none border-2 border-secondary-500 rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Background Process Description</label>
                  <textarea
                    {...register('backgroundProcessDesc')}
                    placeholder="Background process description"
                    className="outline-none border-2 border-secondary-500 rounded-lg w-full"
                    rows={2}
                  />
                </div>
              </div>

              {/* === STATUS === */}
              <SectionHeader title="Status" />
              <div>
                <div className="w-full max-w-2xl">
                  <label className="text-text-hint">
                    Project Status <span className="text-danger-500">*</span>
                  </label>
                  {(() => {
                    const statuses = [...(referenceData?.ProjectStatuses?.status || []).map((s) => s.status), 'OnHold'];
                    const cols = statuses.length || 1;
                    return (
                      <ul className="grid mt-1" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                        {statuses.map((status, idx) => (
                          <li className="relative" key={status}>
                            <input
                              {...register('projectStatus', { required: 'Project Status is required' })}
                              type="radio"
                              value={status}
                              id={`sales_proj_${status.replace(/\s/g, '_')}`}
                              className="sr-only peer"
                            />
                            <label
                              className={`flex justify-center items-center py-2 px-3 text-sm bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 ${
                                projectStatus === status
                                  ? '!bg-secondary-500 text-white border-secondary-800'
                                  : 'text-text-hint'
                              } ${
                                idx === 0
                                  ? 'rounded-tl-full rounded-bl-full'
                                  : idx === statuses.length - 1
                                  ? 'rounded-tr-full rounded-br-full'
                                  : ''
                              }`}
                              htmlFor={`sales_proj_${status.replace(/\s/g, '_')}`}
                            >
                              {status}
                            </label>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                  {errors.projectStatus && (
                    <span className="text-danger-500">{errors.projectStatus.message}</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:gap-8">
                <div className="flex flex-col w-full space-y-1">
                  <label className="text-text-hint mb-1">Status Reason</label>
                  <textarea
                    {...register('statusReason')}
                    placeholder="Status Reason"
                    className="outline-none border-2 border-secondary-500 rounded-lg w-full"
                    rows={3}
                  />
                </div>
              </div>

              {/* === AUDIT FOOTER - Edit Mode Only === */}
              {currentProject.projectId && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-col space-y-1">
                    <label className="text-text-hint text-xs">Created By</label>
                    <p className="text-sm text-gray-600">{currentProject.created_by || 'N/A'}</p>
                  </div>
                  <div className="flex flex-col space-y-1">
                    <label className="text-text-hint text-xs">Date Created</label>
                    <p className="text-sm text-gray-600">
                      {currentProject.created ? formatDateCST(currentProject.created) : 'N/A'}
                    </p>
                    {currentProject.created && (
                      <p className="text-xs text-gray-500">{formatTimeCST(currentProject.created)}</p>
                    )}
                  </div>
                  <div className="flex flex-col space-y-1">
                    <label className="text-text-hint text-xs">Updated By</label>
                    <p className="text-sm text-gray-600">{currentProject.updated_by || 'N/A'}</p>
                  </div>
                  <div className="flex flex-col space-y-1">
                    <label className="text-text-hint text-xs">Last Updated</label>
                    <p className="text-sm text-gray-600">
                      {currentProject.updated ? formatDateCST(currentProject.updated) : 'N/A'}
                    </p>
                    {currentProject.updated && (
                      <p className="text-xs text-gray-500">{formatTimeCST(currentProject.updated)}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-row-reverse justify-between items-center gap-3 w-full pt-4">
                <button
                  type="submit"
                  disabled={!isValid || (currentProject.projectId && !isDirty) || isSaving}
                  className={`py-2 px-6 rounded-lg shadow transition ${
                    isValid && (!currentProject.projectId || isDirty) && !isSaving
                      ? 'bg-secondary-700 text-text-light hover:bg-secondary-800 cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SalesProjects;
